import { flushSync } from 'react-dom';
import {
  FormType,
  useConvertAgentExchangeToSalesAdvice,
  useConvertSalesAdviceToAgentExchange,
} from '@urbanx/agx.campaigns.client';
import {
  AgxButton,
  AgxDialogControlProps,
  AgxModal,
  AgxModalTitleIcon,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_AlertCircleOutline,
  Icon_Save,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';
import type { useFormLoader } from 'hooks/useFormLoader';
import { setAndShowErrorToast } from 'store/config';
import { withCampaignFormConfig } from '../CampaignsView/components/CampaignInfoPanel/Listing/ContractSubmission/ContractSubmitPanel.helpers';
import './ContractReviewAndSend.scss';
import { noContractAvailableWarning } from './NoContractAvailableDialog';

interface ConvertExchangeMethodDialogProps extends AgxDialogControlProps {
  campaignId: string;
  contractId: string;
  blankContractIsLive: boolean;
  destination: FormType.ContractExchange | FormType.ContractSubmission;
  loadFormWithConfig: ReturnType<typeof useFormLoader>['loadFormWithConfig'];
}

const ConvertExchangeMethodDialog = ({
  campaignId,
  contractId,
  blankContractIsLive,
  destination,
  loadFormWithConfig,
  isOpen,
  setIsDialogOpen,
}: ConvertExchangeMethodDialogProps) => {
  const isMobile = useWindowSize() === Breakpoints.Mobile;
  const dispatch = useAppDispatch();
  const markCampaignAsStale = useCampaignStaleMarker();
  const { convertSalesAdviceToAgentExchange, convertSalesAdviceToAgentExchangePending } =
    useConvertSalesAdviceToAgentExchange({
      onSuccess: () => {},
      onError: () => {},
    });
  const { convertAgentExchangeToSalesAdvice, convertAgentExchangeToSalesAdvicePending } =
    useConvertAgentExchangeToSalesAdvice({
      onSuccess: () => {},
      onError: () => {},
    });
  const toAgentExchange = destination === FormType.ContractExchange;
  const offlineContract = toAgentExchange && !blankContractIsLive;
  const actionText = toAgentExchange ? 'Convert to Agent Exchange' : 'Submit as Solicitor Exchange';
  const pending =
    convertSalesAdviceToAgentExchangePending || convertAgentExchangeToSalesAdvicePending;

  const convert = async () => {
    try {
      const convertMethod = toAgentExchange
        ? convertSalesAdviceToAgentExchange
        : convertAgentExchangeToSalesAdvice;
      const form = await convertMethod({ campaignId, contractId });
      if (!form) throw new Error('The exchange form could not be loaded.');

      flushSync(() => setIsDialogOpen(false));
      await markCampaignAsStale(campaignId);
      await loadFormWithConfig(withCampaignFormConfig(form));
    } catch {
      dispatch(
        setAndShowErrorToast(
          toAgentExchange
            ? 'Failed to convert to Agent Exchange'
            : 'Failed to convert to Solicitor Exchange'
        )
      );
    }
  };

  if (!isOpen) return null;

  return (
    <AgxModal
      id="convert-exchange-method-modal"
      className="exchangeMethodConfirmation"
      title={offlineContract ? noContractAvailableWarning.title : `${actionText}?`}
      titleIcon={
        <AgxModalTitleIcon tone="warning">
          {isMobile ? (
            <Icon_Save color={tokens.fg.warning} width={40} height={40} />
          ) : (
            <Icon_AlertCircleOutline type="Warning" height={40} width={40} />
          )}
        </AgxModalTitleIcon>
      }
      type="single"
      maxWidth="600px"
      variant="withoutDivider"
      actionButtonContainerStyle="withDivider"
      setIsDialogOpen={setIsDialogOpen}
      content={
        <AgxSpacer
          padding={{
            left: 'xl',
            right: 'xl',
            bottom: isMobile ? 'spacing-5xl' : 'spacing-6xl',
          }}
        >
          <AgxTypography
            id="exchange-conversion-description"
            type="body_ui"
            size="small"
            color={tokens.text.secondary}
          >
            {offlineContract
              ? noContractAvailableWarning.message
              : toAgentExchange
                ? "Once you confirm, you'll be guided through the next steps to complete the agent exchange."
                : 'This will change Agent Exchange to Solicitor Exchange and void outstanding signing requests. Your existing sale details and deposit information will be retained. You will continue to Contract Submission without the Files page.'}
          </AgxTypography>
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer direction="horizontal" gap="sm" fill>
          <AgxButton
            id="cancel-exchange-conversion"
            text="Cancel"
            variant="hollow"
            size="large"
            disabled={pending}
            onClick={() => setIsDialogOpen(false)}
            stretch
          />
          <AgxButton
            id="confirm-exchange-conversion"
            text={offlineContract ? 'Continue' : 'Confirm'}
            variant="primary"
            size="large"
            disabled={pending}
            onClick={convert}
            stretch
          />
        </AgxSpacer>
      }
    />
  );
};

export default ConvertExchangeMethodDialog;
